/**
 * The React half of {@link AutoLoader}: one hook, four sources.
 *
 * It owns the two browser signals the schedule needs and nothing else — the tab
 * becoming visible again (`document.visibilitychange`, so a plan left open in a
 * background tab polls nothing) and the window regaining focus (which is when a
 * pilot who has been away for an hour wants the answer re-read, not five minutes
 * later on the next beat).
 *
 * `run` is read through a ref, so the fetch always sees the current store and
 * the current language without the schedule being torn down and rebuilt on every
 * render.
 */
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';

import { AutoLoader, type AutoState, type LoadReason } from './autoload';

export interface UseAutoLoad {
  /** the value identity of everything the fetch reads; '' = nothing to ask about */
  signature: string;
  run(ctx: { signal: AbortSignal; reason: LoadReason }): Promise<void>;
  debounceMs: number;
  everyMs?: number;
  staleMs?: number;
}

export interface AutoView extends AutoState {
  /** the one ⟳ */
  refresh(): void;
}

export function useAutoLoad(opts: UseAutoLoad): AutoView {
  const runRef = useRef(opts.run);
  runRef.current = opts.run;

  const loader = useMemo(
    () =>
      new AutoLoader({
        run: ctx => runRef.current(ctx),
        debounceMs: opts.debounceMs,
        ...(opts.everyMs ? { everyMs: opts.everyMs } : {}),
        ...(opts.staleMs ? { staleMs: opts.staleMs } : {}),
      }),
    // the schedule's own constants never change for a given source
    [opts.debounceMs, opts.everyMs, opts.staleMs],
  );

  // the input first, the schedule second: a loader that starts before it knows
  // what to ask would fire one request for the empty signature
  useEffect(() => {
    loader.update(opts.signature);
  }, [loader, opts.signature]);

  useEffect(() => {
    loader.start();
    return () => loader.stop();
  }, [loader]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisible = () => loader.setVisible(document.visibilityState !== 'hidden');
    const onFocus = () => loader.poke();
    loader.setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [loader]);

  const state = useSyncExternalStore(loader.subscribe, loader.getState, loader.getState);

  return useMemo(
    () => ({ ...state, refresh: () => loader.refresh() }),
    [state, loader],
  );
}
